// Lazy, guarded access to @capacitor/core.
//
// Why this exists: libs/core is consumed by both browser apps (which need
// Capacitor at runtime) and the Node-based crm-api (which does not — the
// server only pulls libs/core for pure utilities, but barrel re-exports
// drag in ApiService/AuthService). A top-level `import` of '@capacitor/core'
// causes Node to eagerly resolve the module, and `pnpm deploy --prod`
// prunes it from the API's node_modules — producing a MODULE_NOT_FOUND
// crash on boot.
//
// Detection order (PD-15630 postmortem, 2026-08-23):
// 1. The `window.Capacitor` global. On native, the bridge injects it before
//    any app code runs, so reading it needs no module resolution at all.
//    This is the load-bearing path: the original require-only version
//    silently reported "web" inside Vite-BUNDLED NATIVE APPS, because
//    Vite emits ESM where `require` is undefined and the catch swallowed
//    the ReferenceError — every native branch in ApiService/AuthService/
//    AppUpdateService was dead code in the shipped apps.
// 2. A literal `require('@capacitor/core')` fallback for CommonJS runtimes
//    (webpack/Metro-style bundles), preserving the original behavior there.
// Node/SSR never reaches either: the `window` guard returns first, so the
// server still never resolves the package.
//
// Type-only re-exports below are erased at compile time, so they emit no
// runtime require.

export type { HttpResponse } from '@capacitor/core';
import type { HttpOptions, HttpResponse } from '@capacitor/core';

interface CapacitorLike {
	isNativePlatform: () => boolean;
	getPlatform: () => string;
}

interface CapacitorHttpLike {
	request: (opts: HttpOptions) => Promise<HttpResponse>;
	post: (opts: HttpOptions) => Promise<HttpResponse>;
	get: (opts: HttpOptions) => Promise<HttpResponse>;
}

let _loaded = false;
let _capacitor: CapacitorLike | null = null;
let _capacitorHttp: CapacitorHttpLike | null = null;

/** Shape of the bridge-injected `window.Capacitor` global. */
interface CapacitorGlobalLike extends CapacitorLike {
	Plugins?: {
		CapacitorHttp?: Partial<CapacitorHttpLike>;
	};
}

/**
 * Wrap the raw CapacitorHttp plugin proxy so the convenience verbs exist
 * even when the native proxy only implements `request`.
 */
function _wrapHttp(raw: Partial<CapacitorHttpLike>): CapacitorHttpLike | null {
	const request = raw.request?.bind(raw);
	if (request == null) {
		return null;
	}
	return {
		request,
		get:
			raw.get?.bind(raw) ??
			(async (opts) => request({ ...opts, method: 'GET' })),
		post:
			raw.post?.bind(raw) ??
			(async (opts) => request({ ...opts, method: 'POST' })),
	};
}

function _load(): void {
	if (_loaded) return;
	_loaded = true;

	// Skip on non-browser runtimes (Node/SSR). Capacitor is meaningless there
	// and the package may not be installed in pruned production bundles.
	if (typeof window === 'undefined') return;

	// Preferred: the global the native bridge (or an app-level import of
	// @capacitor/core) installs. Works in every bundler output.
	const injected = (globalThis as { Capacitor?: CapacitorGlobalLike })
		.Capacitor;
	if (injected != null && typeof injected.isNativePlatform === 'function') {
		_capacitor = injected;
		const rawHttp = injected.Plugins?.CapacitorHttp;
		_capacitorHttp = rawHttp != null ? _wrapHttp(rawHttp) : null;
		if (_capacitorHttp != null) {
			return;
		}
		// Platform found but no HTTP plugin on the global (e.g. a web page
		// where nothing registered it) — still try the module for the
		// CapacitorHttp export before giving up on it.
	}

	try {
		// eslint-disable-next-line @typescript-eslint/no-var-requires
		const mod = require('@capacitor/core');
		_capacitor =
			_capacitor ?? (mod?.Capacitor as CapacitorLike | undefined) ?? null;
		_capacitorHttp =
			(mod?.CapacitorHttp as CapacitorHttpLike | undefined) ?? null;
	} catch {
		// Module not resolvable in this runtime (Vite ESM has no `require`).
		// Whatever the global provided above stands; otherwise both stay null.
	}
}

export const Capacitor: CapacitorLike = {
	isNativePlatform: (): boolean => {
		_load();
		return _capacitor?.isNativePlatform() ?? false;
	},
	getPlatform: (): string => {
		_load();
		return _capacitor?.getPlatform() ?? 'web';
	},
};

export const CapacitorHttp: CapacitorHttpLike = {
	request: async (opts) => {
		_load();
		if (_capacitorHttp == null) {
			throw new Error('CapacitorHttp is not available in this runtime');
		}
		return _capacitorHttp.request(opts);
	},
	post: async (opts) => {
		_load();
		if (_capacitorHttp == null) {
			throw new Error('CapacitorHttp is not available in this runtime');
		}
		return _capacitorHttp.post(opts);
	},
	get: async (opts) => {
		_load();
		if (_capacitorHttp == null) {
			throw new Error('CapacitorHttp is not available in this runtime');
		}
		return _capacitorHttp.get(opts);
	},
};
